import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { MdbModalRef, MdbModalService } from 'mdb-angular-ui-kit/modal';
import { CatalogDesignService } from 'src/app/core/services/catalog/catalog-design.service';
import { catchError, finalize, firstValueFrom, of } from 'rxjs';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Store } from '@ngrx/store';
import * as DesignToolActions from '@core/state/design-tool/design-tool.actions';
import { HttpEventType } from '@angular/common/http';
import { selectMobileView } from '@core/state/design-tool/design-tool.selectors';
import { ViewFileUploadJobModalComponent } from '../view-file-upload-job-modal/view-file-upload-job-modal.component';
import { SharedService } from '@shared/shared.service';

export interface FileUploadJob {
  progressMessage: string;
  downloading: boolean;
  errorOnConvert: boolean;
  convertError: any;
  errorOnUpload: boolean;
  uploadError: any;
  hasTransparentBackground: boolean;
  convertedFile: Blob;
  previewImage: string|SafeHtml;
  file: File;
  fileExtension: string;
  name: string;
  uploading: boolean;
  uploadingProgress: number;
  uploaded: boolean;
  converting: boolean;
  convertingProgress: number;
  converted: boolean;
  dimensions: {
    width: number;
    height: number;
  };
  isSvg: boolean;
}
@Component({
  selector: 'app-upload-art-modal',
  templateUrl: './upload-art-modal.component.html',
  styleUrls: ['./upload-art-modal.component.scss']
})
export class UploadArtModalComponent implements OnInit {
  fileUploadJobs: FileUploadJob[] = [];
  selectedFileUploadJob: FileUploadJob;
  uploadedFilesCount = 0
  convertedFilesCount = 0
  isLoadingPreview = false
  finalImagePreviewWidth: number;
  @ViewChild('imagePreview') imagePreviewRef: ElementRef;
  @ViewChild('fileInput') fileInput: ElementRef;
  client: any | null = null;
  mobileView: boolean;
  inputFiles: File[];

  constructor(
    public modalRef: MdbModalRef<UploadArtModalComponent>,
    private sanitizer: DomSanitizer,
    private store: Store,
    private catalogDesignService: CatalogDesignService,
    private sharedService: SharedService,
    private modalService: MdbModalService
  ) {
    this.checkMobileView()
  }

  ngOnInit(): void {
    if (Array.isArray(this.inputFiles) && this.inputFiles?.length > 0) {
      this.onFilesAdded(this.inputFiles)
    }
  }

  async checkMobileView() {
    this.mobileView = await firstValueFrom(this.store.select(selectMobileView))
  }

  onFilesAdded(_files: File[]) {
    const maxSizeInMB = 10; // 10 MB limit
    const fileUploadJobs = _files
      .filter(e => !this.fileUploadJobs.find(f => f.name === e.name))
      .map(e => {
        const fileSizeInMB = e.size / 1024 / 1024;
        if (fileSizeInMB > maxSizeInMB) {
          this.sharedService.showNotification({ 
            data: { title: `${e.name} is too large. Maximum size allowed is 10 MB.`, class: '', 
                    style: { backgroundColor: '#ffc107', color: '#000' } // Inline style
            }, 
            autohide: true 
          });
          return null;
        }
  
        const fileExtension = e.name.split('.').pop().toLowerCase();
        const fileUploadJob = {
          file: e,
          name: e.name.replace(`.${fileExtension}`, ''),
          fileExtension
        } as FileUploadJob;
  
        if (['ai', 'psd', 'pdf'].includes(fileExtension)) {
          fileUploadJob.converting = true;
          fileUploadJob.uploading = true;
          fileUploadJob.convertingProgress = 1;
          fileUploadJob.progressMessage = ' (converting: uploading)';
          fileUploadJob.isSvg = true;
          const originalFileName = fileUploadJob.name;
          this.catalogDesignService.convertFileSVG(fileUploadJob)
            .pipe(
              catchError((err) => {
                console.error('error on converting', err);
                fileUploadJob.errorOnConvert = true;
                fileUploadJob.converting = false;
                fileUploadJob.uploading = false;
                this.convertedFilesCount++;
                fileUploadJob.convertError = err;
                if (this.convertedFilesCount == this.fileUploadJobs.length) {
                  this.isLoadingPreview = false;
                  if (this.fileUploadJobs.some(e => e.errorOnConvert)) {
                    this.sharedService.showNotification({ data: { title: 'Converting process finished with some errors', class: 'bg-danger' }, autohide: true });
                  } else {
                    this.sharedService.showNotification({ data: { title: 'Converting process finished successfully', class: 'bg-success' }, autohide: true });
                  }
                }
                return of(err);
              }),
              finalize(() => fileUploadJob.converting = false)
            )
            .subscribe((event: any) => {
              switch (event.type) {
                case HttpEventType.DownloadProgress:
                  fileUploadJob.downloading = true;
                  fileUploadJob.uploading = false;
                  fileUploadJob.progressMessage = ' (converting: downloading)';
                  if (event.loaded) {
                    fileUploadJob.convertingProgress = event.total ? Math.round(100 * event.loaded / event.total) : 0;
                  }
                  break;
                case HttpEventType.UploadProgress:
                  fileUploadJob.uploading = true;
                  fileUploadJob.downloading = false;
                  if (event.loaded) {
                    fileUploadJob.convertingProgress = event.total ? Math.round(100 * event.loaded / event.total) : 0;
                    fileUploadJob.progressMessage = ' (converting: uploading)';
                    if (fileUploadJob.convertingProgress == 100) {
                      fileUploadJob.progressMessage = ' (converting: processing)';
                    }
                  }
                  break;
                case HttpEventType.Response:
                  fileUploadJob.progressMessage = '';
                  fileUploadJob.convertingProgress = 0;
                  this.convertedFilesCount++;
                  if (this.convertedFilesCount == this.fileUploadJobs.length) {
                    this.isLoadingPreview = false;
                    if (this.fileUploadJobs.some(e => e.errorOnConvert)) {
                      this.sharedService.showNotification({ data: { title: 'Converting process finished with some errors', class: 'bg-danger' }, autohide: true });
                    } else {
                      this.sharedService.showNotification({ data: { title: 'Converting process finished successfully', class: 'bg-success' }, autohide: true });
                    }
                  }
                  fileUploadJob.converted = true;
                  fileUploadJob.converting = false;
                  fileUploadJob.uploading = false;
                  fileUploadJob.fileExtension = 'svg';
                  fileUploadJob.convertedFile = new Blob([event.body], { type: 'image/svg+xml' });
                  this.handleSvgPreview(event.body, fileUploadJob);
                  break;
                default:
                  fileUploadJob.progressMessage = '';
              }
              fileUploadJob.name = originalFileName + fileUploadJob.progressMessage;
            });
        } else {
          fileUploadJob.isSvg = false;
          this.handleRasterImagePreview(fileUploadJob);
        }
        return fileUploadJob;
      })
      .filter(e => e !== null); // Remove null jobs (files that were too large)
  
    this.fileUploadJobs = this.fileUploadJobs.concat(fileUploadJobs);
    if (!this.mobileView) {
      this.selectFileUploadJob(this.fileUploadJobs[0], 0);
    }
  }
  

  onDrop(event: any) {
    // Prevent default behavior (Prevent file from being opened)
    event.preventDefault();

    if (event.dataTransfer.items) {
      // Use DataTransferItemList interface to access the file(s)
      this.onFilesAdded([...event.dataTransfer.items].filter(e => e.kind === "file").map(e => e.getAsFile()))
    } else {
      // Use DataTransfer interface to access the file(s)
      this.onFilesAdded([...event.dataTransfer.files])
    }
  }

  uploadFiles() {
    this.uploadedFilesCount = 0
    const startUpload = (fileUploadJob: FileUploadJob) => {
      fileUploadJob.uploading = true
      this.catalogDesignService.uploadFile(fileUploadJob)
      .pipe(
        catchError((err) => {
          console.error('error on upload', err)
          fileUploadJob.errorOnUpload = true
          this.uploadedFilesCount++
          fileUploadJob.uploadError = err
          if (this.uploadedFilesCount == this.fileUploadJobs.length) {
            this.isLoadingPreview = false
            if (this.fileUploadJobs.some(e => e.errorOnUpload)) {
              this.sharedService.showNotification({ data: { title: 'Upload process finished with some errors', class: 'bg-danger' }, autohide: true });
            } else {
              this.sharedService.showNotification({ data: { title: 'Upload process finished successfully', class: 'bg-success' }, autohide: true });
              this.closeModal();
            }
            this.store.dispatch(DesignToolActions.getDesignLibrary({ client: this.client }));
          }
          return of(err)
        }),
        finalize(() => fileUploadJob.uploading = false)
      )
      .subscribe((event: any) => {
        switch (event.type) {
          case HttpEventType.UploadProgress:{
            // Compute and show the % done:
            fileUploadJob.uploading = true
            if (event.loaded) {
              fileUploadJob.uploadingProgress = event.total ? Math.round(100 * event.loaded / event.total) : 0;
            }
            break}
          case HttpEventType.Response:{
            fileUploadJob.uploadingProgress = 0
            this.uploadedFilesCount++
            if (this.uploadedFilesCount == this.fileUploadJobs.length) {
              if (this.fileUploadJobs.some(e => e.errorOnUpload)) {
                this.sharedService.showNotification({ data: { title: 'Uploading process finished with some errors', class: 'bg-danger' }, autohide: true });
              } else {
                this.sharedService.showNotification({ data: { title: 'Uploading process finished successfully', class: 'bg-success' }, autohide: true });
              }
              this.store.dispatch(DesignToolActions.getDesignLibrary({ client: this.client }));
              this.closeModal()
            }
            fileUploadJob.uploaded = true
            fileUploadJob.uploading = false
            break;}
        }
      })
    }
    this.fileUploadJobs.forEach(fileUploadJob => {
      // upload the original file
      if (fileUploadJob && fileUploadJob.file && !fileUploadJob.errorOnConvert) {
        startUpload(fileUploadJob)
      } else if(fileUploadJob && !fileUploadJob.errorOnConvert && fileUploadJob.converted && fileUploadJob.convertedFile) {// upload the converted file
        fileUploadJob.name = `${fileUploadJob.file.name.split('.').slice(0, -1).join('.')}.svg`;
        startUpload(fileUploadJob)
      } else {
        this.uploadedFilesCount++
      }
    })
  }

  handleSvgPreview(svgData: string, fileUploadJob: FileUploadJob){
    fileUploadJob.previewImage = this.sanitizer.bypassSecurityTrustHtml(svgData);
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgData, 'image/svg+xml');
    const svgElement = doc.documentElement
    let width = svgElement.getAttribute('width');
    let height = svgElement.getAttribute('height');
    if (width.length > 0 && height.length > 0) {
      width = width.replace('px', '')
      height = height.replace('px', '')
      if (!isNaN(Number(width)) && !isNaN(Number(height))) {
        fileUploadJob.dimensions = {
          width: Number(width),
          height: Number(height)
        }
      }
    }
    if (this.selectedFileUploadJob == fileUploadJob) {
      this.onLoadPreview()
    }
  }

  handleRasterImagePreview(fileUploadJob: FileUploadJob) {
    const fileReader: FileReader = new FileReader();
    fileReader.onload = (e: any) => {
      fileUploadJob.previewImage = e.target.result;

      // create new image and set its src to the data URL;
      const img = new Image();
      img.src = fileUploadJob.previewImage as string;

      img.onload = () => {
        // get image dimensions
        const width = img.width;
        const height = img.height;
        this.analyzeImage(fileUploadJob, img)
        fileUploadJob.dimensions = {width, height};
      }
    };
    fileReader.readAsDataURL(fileUploadJob.file);
    fileUploadJob.name = fileUploadJob.file.name.replace(`.${fileUploadJob.fileExtension}`, '');
  }

  async selectFileUploadJob(fileUploadJob: FileUploadJob, index: number) {
    //this.editingJobIndex = index
    if (this.selectedFileUploadJob == fileUploadJob) {
      return
    }
    this.isLoadingPreview = !!fileUploadJob
    this.selectedFileUploadJob = undefined
    setTimeout(() => {
      this.selectedFileUploadJob = fileUploadJob
      if (fileUploadJob?.isSvg && fileUploadJob?.converted) {
        this.onLoadPreview()
      } else if (fileUploadJob?.isSvg && fileUploadJob.errorOnConvert) {
        this.isLoadingPreview = false
      }
    }, 500)
    if (this.mobileView) {
      this.modalService.open(ViewFileUploadJobModalComponent, {
        data: {
          fileUploadJob
        }
      }).onClose.subscribe(resp => {
        if (resp) {
          fileUploadJob = resp
        }
      })
    }
  }

  onLoadPreview(event?: any): void {
    this.finalImagePreviewWidth = undefined
    this.isLoadingPreview = false
    setTimeout(() => {
      this.finalImagePreviewWidth = this.imagePreviewRef.nativeElement.offsetWidth
    }, 100)
  }

  // toggleEdit(event, fileUploadJob: FileUploadJob, index: number) {
  //   event.stopPropagation()
  //   this.selectFileUploadJob(fileUploadJob, index)
  //   this.editingJobIndex = this.editingJobIndex == index ? undefined : index
  // }

  removeFile(event, fileUploadJob: FileUploadJob, index: number) {
    event.stopPropagation()
    if (this.selectedFileUploadJob === fileUploadJob) {
      const nextFile = this.fileUploadJobs?.[index + 1]
      const prevFile = this.fileUploadJobs?.[index - 1]
      if (!nextFile && prevFile) {
        this.selectFileUploadJob(prevFile, index - 1)
      } else if (nextFile) {
        this.selectFileUploadJob(nextFile, index)
      } else {
        this.selectFileUploadJob(undefined, undefined)
      }
    }
    this.fileUploadJobs.splice(index, 1)
    if (this.fileInput?.nativeElement?.value) {
      this.fileInput.nativeElement.value = ""
    }
    if (this.fileUploadJobs.length == 0) {
      this.onClearFiles()
    }
    //this.editingJobIndex = undefined
  }

  onClearFiles() {
    this.fileUploadJobs = []
    this.selectedFileUploadJob = undefined
    this.uploadedFilesCount = 0
    this.convertedFilesCount = 0
    this.isLoadingPreview = false
    this.finalImagePreviewWidth = undefined
    if (this.fileInput?.nativeElement?.value) {
      this.fileInput.nativeElement.value = ""
    }
  }

  getFilesFromFileList(fileList: FileList): File[] {
    return Object.values(fileList)
  }

  doesNotContrastWithWhite(r: number, g: number, b: number, threshold: number): boolean {
    // Pure white components
    const whiteR = 255;
    const whiteG = 255;
    const whiteB = 255;

    // Calculate the euclidian difference between the color and the pure white color
    const diff = Math.sqrt(Math.pow(r - whiteR, 2) + Math.pow(g - whiteG, 2) + Math.pow(b - whiteB, 2));

    // If difference is lower than the threshold, the color does not contrast with white
    return diff < threshold;
  }

  analyzeImage(fileUploadJob: FileUploadJob, img: any): void {
    img.crossOrigin = "Anonymous";
    
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        console.error('Canvas 2D not supported by the browser');
        return;
      }
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0, img.width, img.height);

      // get pixels data
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      // Check the dominant color
      let transparentCount = 0
      for (let i = 0; i < data.length; i += 4) {
        const a = data[i + 3];
        if (a != 255) {
          transparentCount++;
        }
      }
      fileUploadJob.hasTransparentBackground = transparentCount > 0
    };
  }

  onRestart(): void {
    this.onClearFiles()
  }

  closeModal() {
    this.modalRef.close();
  }

  dragOverHandler(ev: any) {
    // Prevent default behavior (Prevent file from being opened)
    ev.preventDefault();
  }

  get isUploading(): boolean {
    return this.fileUploadJobs.some(e => e.uploading)
  }

  get isConverting(): boolean {
    return this.fileUploadJobs.some(e => e.converting)
  }

  get isAllUploaded(): boolean {
    return this.fileUploadJobs.every(e => e.uploaded)
  }

  get canUpload() {
    return !this.isUploading && !this.isConverting && !this.isAllUploaded
  }

  get someConvertingError() {
    return this.fileUploadJobs.some(e => e.errorOnConvert)
  }
}